import { api } from '@lipaflow/shared'

/** Where a payment was found: the bank ledger, a payment attempt, or a payer's report. */
export type HitKind = 'settlement' | 'transaction' | 'proof'

export interface PaymentHit {
  kind: HitKind
  tenant_id: string
  tenant_name: string | null
  id: string
  provider: string | null
  reference: string | null
  amount: string | null
  status: string | null
  phone: string | null
  beneficiary_id: string | null
  occurred_at: string | null
  detail: {
    matched?: boolean | null
    reversed?: boolean
    settlement_event_id?: string | null
    account_reference?: string | null
    failure_reason?: string | null
    reason?: string | null
    channel?: string | null
  }
}

export interface RematchResult {
  settlement_event_id: string
  account_reference: string | null
  matched: boolean
  beneficiary_id: string | null
  beneficiary_name: string | null
  reason: string | null
  applied: boolean
}

export interface Reallocation {
  id: string
  tenant_id: string
  settlement_event_id: string
  amount: string
  reference: string | null
  reason: string
  status: string
  /** Null: raised by the system (a setup deadline). */
  requested_by_user_id: string | null
  decided_by_user_id: string | null
  decided_at: string | null
  extra_data: Record<string, unknown>
  /** Who and where, in words: an approver decides on these. */
  tenant_name: string | null
  from_account: { reference: string; name: string } | null
  to_account: { reference: string; name: string } | null
}

export interface EmailEvent {
  id: string
  record_type: string
  bounce_type: string | null
  is_hard_failure: boolean
  recipient: string
  outcome: string
  description: string | null
  occurred_at: string
}

export interface BouncedUser {
  id: string
  tenant_id: string
  email: string
  email_bounced_at: string
  email_bounce_reason: string | null
}

export interface Account {
  id: string
  name: string
  reference: string | null
}

export interface FailedWebhook {
  id: string
  tenant_id: string | null
  tenant_name: string | null
  provider: string
  event_type: string
  processing_status: 'failed' | 'dlq' | string
  error_message: string | null
  retry_count: number
  received_at: string | null
}

export interface ReplayResult {
  event_id: string
  status_before: string
  status_after: string
  error: string | null
  transaction_id: string | null
}

export interface Conversation {
  id: string
  client_name: string | null
  client_phone: string | null
  state: string
  last_message_at: string | null
  messages: number
}

export interface Transcript {
  session_id: string
  state: string
  messages: { id: string; at: string; direction: 'inbound' | 'outbound' | string; type: string; content: string | null; template: string | null }[]
}

/** The support investigation tools (`/admin/support/*`), all platform-admin
 * and audited. Searches and queues are a sector's (admin sector workspaces). */
export const supportService = {
  searchPayments: async (sector: string, query: { reference?: string; phone?: string }) =>
    (await api.get<PaymentHit[]>('/admin/support/payments/search', {
      params: { ...query, vertical_code: sector, limit: 50 },
    })).data,

  /** A rehearsal unless `apply` is true. */
  rematch: async (settlementId: string, apply: boolean) =>
    (await api.post<RematchResult>(`/admin/support/settlements/${settlementId}/rematch`, null, { params: { apply } })).data,

  searchAccounts: async (tenantId: string, q: string) =>
    (await api.get<Account[]>('/admin/support/accounts', { params: { tenant_id: tenantId, q } })).data,

  requestReallocation: async (body: { settlement_event_id: string; to_beneficiary_id: string; reason: string }) =>
    (await api.post<Reallocation>('/admin/support/allocations/reallocate', body)).data,

  reallocations: async (sector: string, status?: string) =>
    (await api.get<Reallocation[]>('/admin/support/allocations/reallocations', {
      params: { vertical_code: sector, status_filter: status },
    })).data,

  approveReallocation: async (id: string) =>
    (await api.post(`/admin/support/allocations/reallocations/${id}/approve`)).data,

  rejectReallocation: async (id: string, note: string) =>
    (await api.post<Reallocation>(`/admin/support/allocations/reallocations/${id}/reject`, { note })).data,

  emailEvents: async (sector: string, email: string) =>
    (await api.get<EmailEvent[]>('/admin/support/email-events', { params: { vertical_code: sector, email } })).data,

  bouncedUsers: async (sector: string) =>
    (await api.get<BouncedUser[]>('/admin/support/email-bounced-users', { params: { vertical_code: sector } })).data,

  /** Bank and M-PESA messages that failed or gave up retrying. */
  failedWebhooks: async (sector: string) =>
    (await api.get<FailedWebhook[]>('/admin/support/webhooks/failed', { params: { vertical_code: sector } })).data,

  /** Process a recorded message again. Safe: a payment already posted posts nothing. */
  replayWebhook: async (eventId: string) =>
    (await api.post<ReplayResult>(`/admin/support/webhooks/${eventId}/replay`)).data,

  conversations: async (sector: string, tenantId: string) =>
    (await api.get<Conversation[]>('/admin/support/conversations', { params: { vertical_code: sector, tenant_id: tenantId } })).data,

  /** Opening one is recorded in the activity log. */
  transcript: async (sessionId: string) =>
    (await api.get<Transcript>(`/admin/support/conversations/${sessionId}/transcript`)).data,
}
